import React, { ReactNode, Suspense, useMemo } from 'react';
import AccountModule from './account';
import OriginModule from './origin';
import DestinationModule from './destination';
import ExtrasModule from './extras';
import AcceptTermsModule from './accept-terms';
import SenderTypeModule from './sender-type';
import ParcelsModule from './parcels';
import ProtectionModule from './protection';
import ServiceUpgradeModule from './service-upgrade';
import { BasketSections } from '~/lib/types/enums';

const PrintInStoreModule = React.lazy(() => import('./print-in-store'));
const DriverBringsLabels = React.lazy(() => import('./driver-brings-labels'));
const DutiesModule = React.lazy(() => import('./duties'));
const ExportReasonModule = React.lazy(() => import('./export-reason'));
const VatStatusesModule = React.lazy(() => import('./vat-statuses'));
const ParcelContentsModule = React.lazy(() => import('./parcel-contents'));
const DropLocationsModule = React.lazy(() => import('./drop-locations'));
const CollectionDateModule = React.lazy(() => import('./collection-date'));
const CmsContent = React.lazy(() => import('~/components/core/content/cms-content'));

export type BaseModuleProps = {
  errors: Record<string, string>;
  wrapper: (x: ReactNode) => ReactNode;
};

type DynamicModuleProps = BaseModuleProps & {
  moduleKey: string;
};

const ModuleFactory: React.FC<DynamicModuleProps> = ({ moduleKey, errors, wrapper }) => {
  const comp = useMemo(() => {
    if (!moduleKey) return null;
    switch (moduleKey) {
      case 'Account':
        return <AccountModule errors={errors} wrapper={wrapper} />;
      case 'AgreeTerms':
        return <AcceptTermsModule errors={errors} wrapper={wrapper} />;
      case 'Origin':
        return <OriginModule errors={errors} wrapper={wrapper} />;
      case 'Destination':
        return <DestinationModule errors={errors} wrapper={wrapper} />;
      case 'Parcels':
      case 'ParcelsSummary':
        return <ParcelsModule initialSummary={moduleKey === 'ParcelsSummary'} errors={errors} wrapper={wrapper} />;
      case 'Extras':
        return <ExtrasModule errors={errors} wrapper={wrapper} />;
      case 'PrintInStore':
        return <PrintInStoreModule errors={errors} wrapper={wrapper} />;
      case 'DriverBringsLabels':
        return <DriverBringsLabels errors={errors} wrapper={wrapper} />;
      case 'SenderType':
        return <SenderTypeModule errors={errors} wrapper={wrapper} />;
      case 'Protection':
        return <ProtectionModule errors={errors} wrapper={wrapper} />;
      case 'ParcelContents':
        return <ParcelContentsModule errors={errors} wrapper={wrapper} />;
      case 'ExportReason':
        return <ExportReasonModule errors={errors} wrapper={wrapper} />;
      case 'VatStatuses':
        return <VatStatusesModule errors={errors} wrapper={wrapper} />;
      case 'Duties':
        return <DutiesModule errors={errors} wrapper={wrapper} />;
      case 'DropLocations':
        return <DropLocationsModule errors={errors} wrapper={wrapper} type={BasketSections.ORIGIN} />;
      case 'DestinationPickUpLocations':
        return <DropLocationsModule errors={errors} wrapper={wrapper} type={BasketSections.DESTINATION} />;
      case 'CollectionDate':
        return <CollectionDateModule errors={errors} wrapper={wrapper} />;
      case 'ServiceUpgrade':
        return <ServiceUpgradeModule errors={errors} wrapper={wrapper} />;
      default:
        {
          if (moduleKey.startsWith('Content:')) {
            return (
              <Suspense>
                <CmsContent contentKey={moduleKey.split(':')[1]} wrapper={wrapper} />
              </Suspense>
            );
          }
        }
        return null;
    }
  }, [moduleKey, errors, wrapper]);

  if (!moduleKey) return null;

  return <Suspense fallback={<></>}>{comp}</Suspense>;
};

export default ModuleFactory;
